import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type { ImportacionMasiva } from '@/modelos/ImportacionMasiva';

export interface ListaPreciosColumnaDto {
  id: string;
  empresaId: string;
  codigo: string;
  nombre: string;
  modo: string;
  kind: string;
  esBase: boolean;
  esVisible: boolean;
  esVisibleTabla: boolean;
  esPredeterminadoPos: boolean;
  esPredeterminadoComprobante: boolean;
  esUsarEnPos: boolean;
  esUsarEnComprobante: boolean;
  reglaGlobalTipo: string | null;
  reglaGlobalValor: number | null;
  orden: number;
}

export interface ListaPreciosColumnaUpdateDto {
  nombre: string;
  modo: string;
  esVisible: boolean;
  esVisibleTabla: boolean;
  esPredeterminadoPos: boolean;
  esPredeterminadoComprobante: boolean;
  esUsarEnPos: boolean;
  esUsarEnComprobante: boolean;
  reglaGlobalTipo: string | null;
  reglaGlobalValor: number | null;
  orden: number;
}

export interface ListaPreciosPrecioDto {
  id: string;
  empresaId: string;
  sku: string;
  columnaId: string;
  unidadCodigo: string;
  tipo: string;
  valor: number | null;
  rangos: string | null;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface ListaPreciosProductoConPreciosDto {
  sku: string;
  nombre: string;
  unidadCodigo: string;
  unidadSimbolo: string | null;
  unidadNombre: string | null;
  unidadesMedidaAdicionales: string | null;
  precios: ListaPreciosPrecioDto[];
}

export interface ListaPreciosPrecioUpsertDto {
  sku: string;
  columnaId: string;
  unidadCodigo: string;
  tipo: string;
  valor: number | null;
  rangos: string | null;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface ListaPreciosPrecioAprenderResultDto {
  aplicado: boolean;
  precio: ListaPreciosPrecioDto | null;
}

export const listaPreciosService = {
  getColumnas() {
    return httpClient.get<ListaPreciosColumnaDto[]>('/lista-precios/columnas');
  },
  downloadTemplate(columnCodes: string[]): Promise<Blob> {
    const params: Record<string, string> = {};
    if (columnCodes.length > 0) params.columnas = columnCodes.join(',');
    return httpClient.obtenerBlob('/lista-precios/exportar/plantilla', params);
  },
  exportCurrentPrices(): Promise<Blob> {
    return httpClient.obtenerBlob('/lista-precios/exportar/precios-actuales');
  },
  exportarPreciosReporte(params?: { busqueda?: string }): Promise<Blob> {
    return httpClient.obtenerBlob('/reportes/precios/excel', params);
  },
  updateColumna(uuid: string, data: ListaPreciosColumnaUpdateDto) {
    return httpClient.put<ListaPreciosColumnaDto>(`/lista-precios/columnas/${uuid}`, data);
  },
  reordenarColumnas(ids: string[]) {
    return httpClient.post<boolean>('/lista-precios/columnas/reordenar', { ids });
  },
  getPrecios(sku?: string) {
    return httpClient.get<ListaPreciosPrecioDto[]>('/lista-precios/precios', sku ? { sku } : undefined);
  },
  getProductosConPrecios(params?: { page?: number; pageSize?: number; busqueda?: string }) {
    return httpClient.get<ListaPreciosProductoConPreciosDto[]>(
      '/lista-precios/precios/productos-con-precios',
      params as Record<string, string | number | undefined>
    );
  },
  upsertPrecio(data: ListaPreciosPrecioUpsertDto) {
    return httpClient.post<ListaPreciosPrecioDto>('/lista-precios/precios', data);
  },
  aprenderPrecioSiNoExiste(data: ListaPreciosPrecioUpsertDto) {
    return httpClient.post<ListaPreciosPrecioAprenderResultDto>('/lista-precios/precios/aprender-si-no-existe', data);
  },
  bulkUpsertPrecios(data: ListaPreciosPrecioUpsertDto[]) {
    return httpClient.post<ListaPreciosPrecioDto[]>('/lista-precios/precios/bulk-upsert', data);
  },
  registrarImportacion(data: {
    archivoNombre?: string;
    totalRegistros: number;
    creados: number;
    actualizados: number;
    omitidos: number;
  }): Promise<ApiResponse<ImportacionMasiva>> {
    return httpClient.post('/lista-precios/precios/importaciones', data);
  },
  deletePrecio(uuid: string) {
    return httpClient.delete<boolean>(`/lista-precios/precios/${uuid}`);
  },
};
